/**
 * OperationsAssets.tsx — Operations / Assets (M4).
 *
 * The mockup's Assets view (klaxiora-crm-mockup-full.html :1961-1982): four
 * KPI cards, an asset register table (Asset / Category / Assigned to /
 * Status / Next service-renewal with a "Due" badge, :5193-5204), and a
 * New-Asset flow (modal :3130-3176: name, category, assignee people select,
 * status, next service/renewal date). Backed for real by
 * /v1/operations/assets (0102); the assignee is an employee (decision M0),
 * picked from the live active-employee list — the M1-M3 picker pattern.
 *
 * Two deliberate divergences from the mockup, both decision M4: rows carry
 * an EDIT affordance reusing the same drawer (the mockup is create-only,
 * but its own Equipment Checkout SOP :5296 describes reassignment and
 * status changes — full-edit PUT, unassign and clear-date included), and
 * status carries 'Retired' beyond the mockup's three — end-of-life without
 * a delete (0102 grants none). The attention KPI runs against the real
 * clock and excludes retired assets (assetsView owns the predicate).
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { OpsMetric } from './OperationsOverview'
import { useAsync, fetchAssets, createAsset, updateAsset, fetchDirectory } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { overlay, success, danger } from '../../../../theme/tokens'
import type { OpsAsset } from '../../types'
import { ASSET_CATEGORIES, ASSET_STATUSES, assetCategoryLabel, assetStatusLabel, assetCountLine, assetNeedsAttention, assetKpis } from './assetsView'
import { formatSopDate } from './sopsView'

// ── Create / Edit drawer (the SopDrawer pattern — full-replace PUT) ──────────

function AssetDrawer({ asset, onClose, onSaved }: {
  asset: OpsAsset | null // null = create
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(asset?.name ?? '')
  const [category, setCategory] = useState(asset?.category ?? 'laptop')
  const [status, setStatus] = useState(asset?.status ?? 'in_use')
  const [assignedTo, setAssignedTo] = useState(asset?.assignedTo ?? '')
  const [nextServiceDate, setNextServiceDate] = useState(asset?.nextServiceDate ?? '')

  // Assignee picklist = live ACTIVE employees with an Unassigned blank —
  // the mockup's fillPeopleSelect(select, 'Unassigned') (:5137).
  const employees = useAsync(() => fetchDirectory({ status: 'active' }), [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function handleSave() {
    if (!name.trim()) { toast.error('Name is required'); return }
    // Full-replace write: an empty assignee/date is OMITTED, which the
    // server nulls — this is how unassign and clear-date work (decision M4).
    const write = {
      name: name.trim(), category, status,
      assignedTo: assignedTo || undefined,
      nextServiceDate: nextServiceDate || undefined,
    }
    try {
      if (asset) {
        await updateAsset(asset.id, write)
        toast.success('Asset updated')
      } else {
        await createAsset(write)
        toast.success('Asset added')
      }
      onSaved()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, asset ? 'Failed to update asset' : 'Failed to add asset'))
    }
  }

  // bg-surface-control + ink-body is the PROVEN portal-form recipe
  // (FinanceExpenses.tsx:46, SalesDealDetail.tsx:320): a portal renders
  // outside .dash-shell, so without an explicit background token the UA
  // paints inputs white and the light ink tokens vanish into them.
  const inputCls = 'w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all'
  const labelCls = 'block text-12 font-semibold text-ink-body mb-2'

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      {/* PORTAL RULE (theme/theme.ts): no colorScheme here. data-theme +
          color-scheme are stamped on <html>, so this portal subtree inherits
          both — UA-drawn select popups and picker chrome follow the ACTIVE
          theme instead of a hardcoded 'dark' that would invert in light mode. */}
      <div className="fixed top-0 right-0 bg-surface-card border-l border-edge-row flex flex-col"
        style={{ height: '100dvh', width: 'min(580px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">{asset ? 'Edit Asset' : 'New Asset'}</div>
            <div className="text-12 text-ink-muted mt-1">
              {asset ? 'Reassign it, change its status, or correct the record.' : 'Add a piece of equipment, a tool, or a licence to the register.'}
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div>
            <label className={labelCls}>Name<span className="text-danger ml-1">*</span></label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder={'e.g. MacBook Pro 14" — #4471'} autoFocus className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} className={inputCls}>
                {ASSET_CATEGORIES.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Assigned to</label>
              {employees.error ? (
                <div className="text-12 text-ink-muted px-1 py-3">Couldn't load employees</div>
              ) : (
                <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)}
                  disabled={employees.loading} className={inputCls}>
                  <option value="">Unassigned</option>
                  {(employees.data ?? []).map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.fullName}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className={inputCls}>
                {ASSET_STATUSES.map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Next service / renewal</label>
              <input type="date" value={nextServiceDate} onChange={e => setNextServiceDate(e.target.value)}
                className={inputCls} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose}
            className="k-btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave}
            className="k-btn">
            {asset ? 'Save Changes' : 'Save Asset'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

function AssetStatusBadge({ status }: { status: string }) {
  // The mockup's badge rule (:5197): In Use = won (success), Maintenance =
  // lost (danger), Available = plain; Retired renders muted — end-of-life.
  if (status === 'in_use') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-11 font-medium"
        style={{ background: success.bg, color: success.text }}>
        In Use
      </span>
    )
  }
  if (status === 'maintenance') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-11 font-medium"
        style={{ background: danger.bg, color: danger.text }}>
        Maintenance
      </span>
    )
  }
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-11 font-medium bg-surface-panel ${status === 'retired' ? 'text-ink-faint' : 'text-ink-secondary'}`}>
      {assetStatusLabel(status)}
    </span>
  )
}

export function OperationsAssets() {
  const [reloadKey, setReloadKey] = useState(0)
  const [drawer, setDrawer] = useState<{ open: boolean; asset: OpsAsset | null }>({ open: false, asset: null })
  const { data, loading, error } = useAsync(fetchAssets, [reloadKey])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const assets = data ?? []
  const now = new Date()
  const kpis = assetKpis(assets, now)

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Assets"
        subtitle={assets.length === 0 ? 'Equipment, tools, and licenses the business owns.' : assetCountLine(assets.length)}
        action={
          <button className="k-btn inline-flex items-center gap-2" onClick={() => setDrawer({ open: true, asset: null })}>
            <Plus size={14} /> New Asset
          </button>
        }
      />

      <div className="grid grid-cols-4 gap-3">
        <OpsMetric label="Total assets" value={kpis.total} note="On file" />
        <OpsMetric label="Assigned" value={kpis.assigned} note="In someone's hands" />
        <OpsMetric label="Available" value={kpis.available} note="Not checked out" />
        <OpsMetric label="Needs attention" value={kpis.attention} note="Maintenance or renewal due" />
      </div>

      <div className="k-card">
        {assets.length === 0 ? (
          <EmptyState
            message="No assets yet"
            hint="Equipment, tools, and licences you add will appear here"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="closr-table ops-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Asset</th>
                  <th className="text-left">Category</th>
                  <th className="text-left">Assigned to</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Next service / renewal</th>
                  <th className="text-left"></th>
                </tr>
              </thead>
              <tbody>
                {assets.map(a => (
                  <tr key={a.id}>
                    <td className="text-13 font-medium text-ink-primary">{a.name}</td>
                    <td className="text-12 text-ink-secondary">{assetCategoryLabel(a.category)}</td>
                    <td className="text-12 text-ink-secondary">{a.assignedToName || '—'}</td>
                    <td><AssetStatusBadge status={a.status} /></td>
                    <td className="text-12 text-ink-secondary whitespace-nowrap">
                      {a.nextServiceDate ? formatSopDate(a.nextServiceDate) : '—'}
                      {assetNeedsAttention(a, now) && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-11 font-medium ml-2"
                          style={{ background: danger.bg, color: danger.text }}>
                          Due
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <button className="k-btn-secondary" onClick={() => setDrawer({ open: true, asset: a })}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {drawer.open && (
        <AssetDrawer
          asset={drawer.asset}
          onClose={() => setDrawer({ open: false, asset: null })}
          onSaved={() => setReloadKey(k => k + 1)}
        />
      )}
    </div>
  )
}
